import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import Select from 'react-select';
import store from '../stores/NotesStore';
import { fmt } from '../helpers/helpers';
import { fetchIncomeCandidates } from '../helpers/api';
import './PaymentSourceModal.css';

const PaymentSourceModal = observer(({ entry, onClose, mode = 'expense' }) => {
  const isIncomeMode = mode === 'income';
  const [candidateGroups, setCandidateGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const sources = isIncomeMode ? store.sourcesForIncome(entry.id) : store.sourcesForExpense(entry.id);
  const totalAllocated = sources.reduce((a, s) => a + s.amount, 0);

  const loadCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchIncomeCandidates(store.userId, store.currentNoteId);
      const groups = (data.notes || [])
        .map(note => ({
          label: note.noteTitle,
          options: note.entries.map(e => ({
            value: `${note.noteId}:${e.entryId}`,
            label: `${e.label} · ${fmt(e.value)}${e.allocated > 0 ? ` (alocado: ${fmt(e.allocated)})` : ''}`,
            noteId: note.noteId,
            entryId: e.entryId
          }))
        }))
        .filter(group => group.options.length > 0);
      setCandidateGroups(groups);
    } catch (error) {
      console.error('Error loading income candidates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Income mode is read-only: no picker, so no candidates to load.
    if (!isIncomeMode) loadCandidates();
  }, [isIncomeMode, loadCandidates]);

  const handleSelect = async (option) => {
    if (!option) return;
    setSelectedOption(null);
    setLoading(true);
    try {
      await store.addEntrySource({
        expenseNoteId: store.currentNoteId,
        expenseEntryId: entry.id,
        incomeNoteId: option.noteId,
        incomeEntryId: option.entryId
      });
      await loadCandidates();
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id) => {
    setLoading(true);
    try {
      await store.removeEntrySource(id);
      await loadCandidates();
    } finally {
      setLoading(false);
    }
  };

  const handleAmountCommit = async (id, currentAmount, rawValue) => {
    const parsed = parseFloat(rawValue);
    if (!Number.isFinite(parsed) || parsed <= 0 || parsed === currentAmount) return;
    await store.changeEntrySourceAmount(id, parsed);
    await loadCandidates();
  };

  const entryLabel = entry.customName || entry.label || 'Sem nome';
  const multiSource = !isIncomeMode && sources.length >= 2;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="payment-source-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isIncomeMode ? 'Usado para pagar' : 'Pago com'} — {entryLabel}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          <div className="source-value-hint">{fmt(entry.value)}</div>

          <div className="source-list">
            {sources.length === 0 && (
              <div className="source-empty">Nenhuma fonte vinculada</div>
            )}
            {sources.map(s => {
              const other = isIncomeMode ? s.expense : s.income;
              return (
                <div className="source-row" key={s.id}>
                  <div className="source-row-info">
                    {other.missing ? (
                      <span className="source-missing">Entrada removida</span>
                    ) : (
                      <>
                        <span className="source-label">{other.label}</span>
                        {!other.inCurrentNote && (
                          <span className="source-note-title">{other.noteTitle}</span>
                        )}
                      </>
                    )}
                  </div>
                  {multiSource ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="source-amount-input"
                      defaultValue={s.amount}
                      disabled={loading}
                      onBlur={(e) => handleAmountCommit(s.id, s.amount, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                      }}
                    />
                  ) : (
                    <span className="source-amount">{fmt(s.amount)}</span>
                  )}
                  {!isIncomeMode && (
                    <button
                      className="source-remove-btn"
                      onClick={() => handleRemove(s.id)}
                      disabled={loading}
                      title="Remover fonte"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {!isIncomeMode && (
            <div className="form-section">
              <label className="form-label">Adicionar fonte</label>
              <Select
                isClearable
                isDisabled={loading}
                isLoading={loading}
                onChange={handleSelect}
                options={candidateGroups}
                value={selectedOption}
                placeholder="Selecione uma receita..."
                noOptionsMessage={() => 'Nenhuma receita encontrada'}
                styles={{
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
                }}
                menuPortalTarget={document.body}
              />
            </div>
          )}

          <div className="source-readout">
            {isIncomeMode ? 'Comprometido' : 'Alocado'}: {fmt(totalAllocated)} de {fmt(Math.abs(entry.value))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
});

export default PaymentSourceModal;
